import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const ForcePasswordChange = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requirements, setRequirements] = useState([]);

  React.useEffect(() => {
    // Fetch password requirements
    const fetchRequirements = async () => {
      try {
        const response = await axios.get(`${API}/auth/password-requirements`);
        setRequirements(response.data.requirements || []);
      } catch (err) {
        console.error('Failed to fetch password requirements');
      }
    };

    fetchRequirements();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passordene matcher ikke');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API}/auth/change-password`,
        {
          old_password: oldPassword,
          new_password: newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Password changed successfully - redirect to dashboard
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke endre passord. Sjekk at du har skrevet riktig nåværende passord.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <AlertTriangle className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">Passordendring påkrevd</CardTitle>
          <CardDescription className="text-gray-400">
            Du må endre passordet ditt før du kan fortsette
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="bg-orange-900/20 border-orange-600 mb-4">
            <AlertDescription className="text-orange-400">
              <strong>Sikkerhetskrav:</strong> Av sikkerhetsmessige årsaker må du endre det midlertidige passordet ditt nå.
            </AlertDescription>
          </Alert>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert className="bg-red-900/20 border-red-600">
                <AlertDescription className="text-red-400">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <label htmlFor="oldPassword" className="text-sm font-medium text-gray-300">
                Nåværende passord (midlertidig)
              </label>
              <Input
                id="oldPassword"
                type={showPasswords ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white"
                disabled={loading}
                placeholder="Skriv inn det midlertidige passordet du fikk"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="newPassword" className="text-sm font-medium text-gray-300">
                Nytt passord
              </label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPasswords ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white pr-10"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-300">
                Bekreft nytt passord
              </label>
              <Input
                id="confirmPassword"
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white"
                disabled={loading}
              />
            </div>

            {requirements.length > 0 && (
              <div className="bg-gray-700/50 p-3 rounded-lg">
                <p className="text-xs font-medium text-gray-300 mb-2">Passordkrav:</p>
                <ul className="text-xs text-gray-400 space-y-1">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-orange-500 mr-2">•</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800"
              disabled={loading}
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? 'Endrer passord...' : 'Endre passord'}
            </Button>

            <Button
              type="button"
              onClick={handleLogout}
              variant="outline"
              className="w-full border-gray-600 text-white hover:bg-gray-700"
              disabled={loading}
            >
              Logg ut
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForcePasswordChange;
